/**
 * The help menu: articles come from the website's help centre (blanqo-web,
 * `<siteUrl>/help/index.json`, English and German), "Contact us" goes to support through
 * server/pb_hooks/support.pb.js.
 */
import { env } from "@/lib/core/env";
import { pb } from "@/lib/core/pocketbase";

export type HelpLang = "en" | "de";
export type HelpArticle = {
  lang: HelpLang;
  key: string;
  category: string;
  title: string;
  description: string;
  url: string;
  text: string;
};

/** German UI gets the German articles, every other language the English ones. */
export const helpLanguage = (appLanguage: string): HelpLang =>
  appLanguage.startsWith("de") ? "de" : "en";

export const helpCentreUrl = (lang: HelpLang) =>
  `${env.siteUrl}${lang === "de" ? "/de/hilfe" : "/help"}`;

export async function fetchHelpIndex(): Promise<HelpArticle[]> {
  const res = await fetch(`${env.siteUrl}/help/index.json`);
  if (!res.ok) throw new Error(`Help articles could not be loaded (${res.status})`);
  return ((await res.json()) as { articles: HelpArticle[] }).articles;
}

const normalize = (text: string) => text.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/**
 * The articles in `lang` that contain every word of the query, best first (title over
 * description over text); without a query all of them in the help centre's order.
 */
export function searchHelp(
  articles: HelpArticle[],
  query: string,
  lang: HelpLang,
  limit = 8,
): HelpArticle[] {
  const own = articles.filter((article) => article.lang === lang);
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return own.slice(0, limit);
  return own
    .map((article) => {
      const title = normalize(article.title);
      const description = normalize(article.description);
      const text = normalize(article.text);
      let score = 0;
      for (const word of words) {
        const hit =
          (title.includes(word) ? 3 : 0) +
          (description.includes(word) ? 2 : 0) +
          (text.includes(word) ? 1 : 0);
        if (hit === 0) return { article, score: 0 };
        score += hit;
      }
      return { article, score };
    })
    .filter((result) => result.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((result) => result.article);
}

export async function sendSupportMessage(input: {
  subject: string;
  message: string;
  page: string;
}) {
  return pb.send<{ sent: boolean }>("/api/blanqo/support", {
    method: "POST",
    body: { ...input, version: env.appVersion },
  });
}
